import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { ListPropertyInquiriesResponse, PropertyInquiry } from '@immo/api-contract';
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import { ARIA_SORT, SortableHeader, useTableSort, type SortValue } from '@/components/sortable-table';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';

const NS = 'pages.admin.properties.detail.inquiries';

const FIELDS = ['receivedAt', 'source', 'name', 'email', 'phone', 'message', 'outcome'] as const;

type Field = (typeof FIELDS)[number];

const COLUMN_KEY: Record<Field, string> = {
  receivedAt: 'received-at',
  source: 'source',
  name: 'name',
  email: 'email',
  phone: 'phone',
  message: 'message',
  outcome: 'outcome',
};

const EMPTY_CELL = '—';

/** The Anfragen tab's fetch state; every state past `idle` names the Objekt it was fetched for. */
export type InquiriesState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading'; readonly propertyId: string }
  | { readonly status: 'loaded'; readonly propertyId: string; readonly data: ListPropertyInquiriesResponse }
  | { readonly status: 'forbidden'; readonly propertyId: string }
  | { readonly status: 'failed'; readonly propertyId: string };

export type InquiriesController = {
  readonly state: InquiriesState;
  /** Called when the tab is activated. Loads only when nothing was fetched for this Objekt yet. */
  readonly open: () => void;
  /** Forces a refetch after a transient failure. */
  readonly retry: () => void;
};

/** Fetch-once-on-open, lifted into the detail page so switching tabs away and back issues no second request. */
export function useInquiriesController(propertyId: string): InquiriesController {
  const [state, setState] = useState<InquiriesState>({ status: 'idle' });
  const inFlightFor = useRef<string | null>(null);
  const currentPropertyId = useRef(propertyId);

  currentPropertyId.current = propertyId;

  const load = useCallback(async () => {
    if (inFlightFor.current === propertyId) return;

    inFlightFor.current = propertyId;
    setState({ status: 'loading', propertyId });

    try {
      const result = await apiClient.adminProperties.inquiries({ pathParams: { id: propertyId } });

      if (currentPropertyId.current !== propertyId) return;

      if (result.ok) {
        setState({ status: 'loaded', propertyId, data: result.body });
      } else if (result.status === 403) {
        setState({ status: 'forbidden', propertyId });
      } else {
        setState({ status: 'failed', propertyId });
      }
    } catch {
      if (currentPropertyId.current === propertyId) setState({ status: 'failed', propertyId });
    } finally {
      if (inFlightFor.current === propertyId) inFlightFor.current = null;
    }
  }, [propertyId]);

  const open = useCallback(() => {
    if (state.status !== 'idle' && state.propertyId === propertyId) return;

    void load();
  }, [state, propertyId, load]);

  const retry = useCallback(() => {
    void load();
  }, [load]);

  return useMemo(() => ({ state, open, retry }), [state, open, retry]);
}

type InquiriesTabProps = {
  readonly propertyId: string;
  readonly state: InquiriesState;
  readonly onRetry: () => void;
};

/** Every inquiry an Objekt received and what became of it, read-only. */
export function InquiriesTab({ propertyId, state, onRetry }: InquiriesTabProps): ReactNode {
  const t = useTranslations(NS);

  if (state.status === 'idle' || state.propertyId !== propertyId || state.status === 'loading') {
    return <p className="py-10 text-center text-base-sm text-text-tertiary">{t('loading')}</p>;
  }

  if (state.status === 'forbidden') {
    return (
      <div className="rounded-md border border-neutral-200 bg-canvas px-4 py-10 text-center text-base-sm text-text-secondary">
        {t('forbidden')}
      </div>
    );
  }

  if (state.status === 'failed') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-md border border-neutral-200 bg-canvas px-4 py-10 text-center">
        <p className="text-base-sm text-text-secondary">{t('error')}</p>
        <Button variant="secondary" onClick={onRetry}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  return <InquiriesView data={state.data.data} />;
}

function InquiriesView({ data }: { data: ReadonlyArray<PropertyInquiry> }): ReactNode {
  const t = useTranslations(NS);
  const intl = useIntl();

  const sortKey = useCallback(
    (inquiry: PropertyInquiry, field: Field): SortValue => {
      switch (field) {
        case 'receivedAt':
          return Date.parse(inquiry.receivedAt);
        case 'source':
          return t(`source.${inquiry.source}`);
        case 'name':
          return inquiry.name;
        case 'email':
          return inquiry.email;
        case 'phone':
          return inquiry.phone;
        case 'message':
          return inquiry.message;
        case 'outcome':
          return t(`outcome.${inquiry.outcome}`);
      }
    },
    [t],
  );

  const { sorted, sort, toggleSort } = useTableSort(data, sortKey);

  if (data.length === 0) {
    return <p className="py-8 text-center text-base-sm text-text-tertiary">{t('empty')}</p>;
  }

  return (
    <Table scrollX>
      <TableHeader>
        <TableRow>
          {FIELDS.map((field) => (
            <TableHead
              key={field}
              {...(sort !== null && sort.field === field ? { 'aria-sort': ARIA_SORT[sort.direction] } : {})}
            >
              <SortableHeader
                field={field}
                label={t(`columns.${COLUMN_KEY[field]}`)}
                ariaLabel={t('sort-label', { column: t(`columns.${COLUMN_KEY[field]}`) })}
                sort={sort}
                onSort={toggleSort}
              />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((inquiry) => (
          <TableRow key={inquiry.id}>
            <TableCell className="text-text-secondary">
              {intl.formatDate(inquiry.receivedAt, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </TableCell>
            <TableCell className="text-text-secondary">{t(`source.${inquiry.source}`)}</TableCell>
            <TableCell className="text-text-primary">{inquiry.name}</TableCell>
            <TableCell className="text-text-primary">{inquiry.email}</TableCell>
            <TableCell className="text-text-secondary">{inquiry.phone ?? EMPTY_CELL}</TableCell>
            <TableCell className="text-text-secondary">
              {inquiry.message === null ? (
                EMPTY_CELL
              ) : (
                <span className="line-clamp-2 block max-w-xs whitespace-normal" title={inquiry.message}>
                  {inquiry.message}
                </span>
              )}
            </TableCell>
            <TableCell className="text-text-primary">{t(`outcome.${inquiry.outcome}`)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
